import { FileText, UploadIcon, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import apiCall from "../network/api";
import { useNavigate } from "react-router-dom";
import { SMARTSHEET_URL } from "../network/config";
import { loader } from "../utils/svg";

const Upload = ({
  document,
  bgColor,
  imgBgColor,
  selectedImage,
  setSelectedImage,
  id,
  currentStep = "",
  smartSheetLinks,
  setSmartSheetLinks,
  shouldUpload = true,
  maxLimit = 5,
  showErrorOnLimitExceed = true,
  fileUploads,
  setFileUploads,
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const inputId = `file-input-${id}-${currentStep}`;

  useEffect(() => {
    setError(false);
  }, [currentStep]);

  function trimFilename(filename) {
    if (filename?.length > 24) {
      return (
        filename?.slice(0, 14) +
        "..." +
        filename?.split(".")?.[filename?.split(".")?.length - 1]
      );
    }
    return filename;
  }

  const handleImageChange = (event, currentStep) => {
    const files = event.target.files;
    const stepFolder = `m${currentStep}`; // lock step value

    const images = Array.isArray(selectedImage) ? selectedImage : [];

    if (Array.from(files).length + images.length > maxLimit) {
      setError(`You can only upload a maximum of ${maxLimit} files.`);
      event.target.value = null;
      return;
    }

    let newImages = [];

    Array.from(files).forEach((file) => {
      const isDuplicate =
        images.findIndex(
          (image) =>
            image?.name ===
              `${encodeURIComponent(id)}/${stepFolder}/${encodeURIComponent(
                file.name
              )}` || image?.name === encodeURIComponent(file.name)
        ) !== -1;

      if (!isDuplicate) {
        if (file.size > 5 * 1024 * 1024) {
          setError("All selected files must be 5 MB or smaller.");
          return;
        }

        const upload = {
          step: stepFolder,
          image: URL.createObjectURL(file),
          name: encodeURIComponent(file.name),
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          lastModifiedDate: file.lastModifiedDate,
          webkitRelativePath: file.webkitRelativePath,
        };

        setFileUploads((prev) => [
          ...prev.filter(
            (uploadedFile) =>
              uploadedFile?.name !== encodeURIComponent(file.name)
          ),
          { name: encodeURIComponent(file.name), uploaded: "loading" },
        ]);

        // pass locked value
        uploadFile(file, id, stepFolder);

        newImages.push(upload);
      }
    });

    event.target.value = null;

    if (newImages.length > 0) {
      setSelectedImage([...images, ...newImages]);
    }
  };
  const getPresignedUrl = async (
    folderName,
    subFolderName,
    fileName,
    fileType
  ) => {
    try {
      const result = apiCall({
        endpoint: "/generate-presigned-url",
        method: "POST",
        data: JSON.stringify({ folderName, subFolderName, fileName, fileType }),
      }).then((data) => {
        return data?.url;
      });
      return result;
    } catch (err) {
      setError(err);
      if (err?.status === 403) {
        localStorage.clear();
        navigate("/login");
      }
    }
  };

  const uploadFile = async (file, folderName, subFolderName) => {
    const url = await getPresignedUrl(
      encodeURIComponent(folderName),
      subFolderName,
      encodeURIComponent(file.name),
      file.type
    );

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });

    if (response.ok) {
      setSmartSheetLinks((prev) => [
        ...prev,
        `${SMARTSHEET_URL}/${encodeURIComponent(folderName)}/${subFolderName}/${
          file.name
        }`,
      ]);

      setFileUploads((prev) => [
        ...[...prev]?.filter(
          (uploadedFile) => uploadedFile?.name !== encodeURIComponent(file.name)
        ),
        { name: encodeURIComponent(file.name), uploaded: "completed" },
      ]);

      console.log("File uploaded successfully");
    } else {
      setError(response?.statusText);
      //   console.error("File upload failed", response.statusText);
    }
  };

  const deleteFile = async (fileName, indexToRemove, currentStep) => {
    try {
      apiCall({
        endpoint: "/delete-file",
        method: "DELETE",
        data: JSON.stringify({
          fileName: `${encodeURIComponent(id)}/m${currentStep}/${fileName}`,
        }),
      }).then((data) => {
        if (data?.success) {
          // Update the file list after deletion
          const folder = `m${currentStep}`;

          // files belonging to this step
          const filesInFolder = selectedImage.filter(
            (file) => file.step === folder || file.name?.includes(`/${folder}/`)
          );

          // remove the selected file
          const updatedFolderFiles = filesInFolder.filter(
            (_, index) => index !== indexToRemove
          );

          // keep files from other steps
          const otherFiles = selectedImage.filter(
            (file) =>
              !(file.step === folder || file.name?.includes(`/${folder}/`))
          );

          // merge back
          setSelectedImage([...otherFiles, ...updatedFolderFiles]);

          setSmartSheetLinks(
            smartSheetLinks.filter((file, index) => index !== indexToRemove)
          );
        }
      });
    } catch (error) {
      setError(error?.message);
      //   console.error("Error deleting file:", error?.message);
    }
  };

  const removeFile = (name, indexToRemove, currentStep) => {
    deleteFile(name, indexToRemove, currentStep);
  };

  const filterByFolder = (data, folder) => {
    return [
      ...data.filter((item) => item.name.includes(`/${folder}/`)),
      ...selectedImage?.filter((file) => file.step === folder),
    ];
  };

  return (
    <div>
      <div
        className={`border border-dashed border-gray-300 rounded-lg py-4 px-8 text-center ${bgColor}`}
      >
        <form>
          <label htmlFor={inputId} style={{ cursor: "pointer" }}>
            <UploadIcon className="w-6 h-6 mx-auto mb-2 text-gray-500" />
            <p className="text-sm font-medium mb-1 text-gray-500">
              Please upload {document.doc_type === "image" ? "photo" : "file"}{" "}
              here
            </p>
          </label>
          <input
            id={inputId}
            type="file"
            multiple
            accept={
              document.doc_type === "image"
                ? "image/*"
                : document.doc_type === "pdf"
                  ? ".pdf,application/pdf"
                  : "*"
            }
            onInput={(e) => handleImageChange(e, currentStep)}
            style={{ display: "none" }} // Hide the input
          />
        </form>
        <p className="text-xs text-gray-400">
          {document.doc_type === "image"
            ? "Image must be in .jpg, .png, .heic and should be less than 5mb"
            : "File should be less than 5mb"}
        </p>
      </div>
      {error && (
        <div className="bg-customError-100 ring-1 ring-customError-200 rounded-md p-3 mt-3">
          <p className="text-sm font-semibold text-customError-500">{error}</p>
        </div>
      )}
      {filterByFolder(selectedImage, `m${currentStep}`)?.length > 0 && (
        <div className="flex flex-col gap-3 pt-3">
          {filterByFolder(selectedImage, `m${currentStep}`)?.map(
            (image, index) => {
              let name =
                image?.name?.split("/")?.[image?.name?.split("/")?.length - 1];
              return (
                <div
                  className={`flex items-center justify-between border rounded-lg p-3 gap-3 ${imgBgColor}`}
                >
                  <div className="flex gap-3">
                    <div className="w-12 max-w-12 rounded-lg flex items-center justify-center h-12">
                      {document.doc_type === "image" ? (
                        <img
                          key={index}
                          src={image?.image ? image?.image : image?.url}
                          alt={`upload-${index}`}
                          className="w-12 max-w-12 rounded-lg object-contain h-12"
                        />
                      ) : (
                        <div className="w-12 max-w-12 rounded-lg object-contain h-10 flex justify-center items-center text-[#4592FD]">
                          <FileText />
                        </div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm truncate">
                        {trimFilename(name, image?.type)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(image?.size / (1024 * 1024)).toFixed(3)} MB
                      </p>
                    </div>
                  </div>
                  {fileUploads?.find(
                    (uploadedFile) => uploadedFile?.name === name
                  )?.uploaded === "loading" ? (
                    <>{loader()}</>
                  ) : (
                    <button
                      id={currentStep}
                      onClick={() => removeFile(name, index, currentStep)}
                      className="text-customError-400 hover:text-customError-500 w-4"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
};

export default Upload;
